import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ListRenderItemInfo, Pressable, StyleSheet, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { getScreenSize } from 'utils/responsiveness';

import { SCREEN_PLAYER_MANAGE_RECORDING_OVERLAY_TITLE } from 'screens/Detail/languageConst';
import { ManageRecordingItem } from 'screens/Detail/types';

import Section from '../RecordingActionsOverlay/components/Section';
import SelectionOption from '../RecordingActionsOverlay/components/SelectionOption';
import { IProps } from '../types';
import styles, {
    MARGIN_RIGHT,
    MARGIN_TOP,
    MAX_MENU_WIDTH,
    SVG_HALF_WIDTH,
} from './styles';

const {
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_CLOSE_RECORDINGS_OVERLAY },
    },
} = LANGUAGE_KEYS;

const RecordingActionsDropDown = ({
    onRecordingItemPress,
    recordingActionButtonPosition,
    onClose,
    isVisible,
    recordingItems,
}: IProps) => {
    const { t } = useTranslation();
    const accessibleViewRef = useRef(null);

    const positionStyle = useMemo(() => {
        return (
            recordingActionButtonPosition && {
                top: recordingActionButtonPosition.y + MARGIN_TOP,
                left: getScreenSize('width') - MAX_MENU_WIDTH - MARGIN_RIGHT,
            }
        );
    }, [recordingActionButtonPosition]);

    const rightPositionForArrow = useMemo(() => {
        return (
            recordingActionButtonPosition && {
                right:
                    getScreenSize('width') -
                    recordingActionButtonPosition.x -
                    MARGIN_RIGHT -
                    recordingActionButtonPosition.width +
                    SVG_HALF_WIDTH,
            }
        );
    }, [recordingActionButtonPosition]);

    const renderRecordingItem = useCallback(
        ({ item }: ListRenderItemInfo<ManageRecordingItem>) => {
            return (
                <SelectionOption
                    key={item.key}
                    item={item}
                    onPress={onRecordingItemPress}
                />
            );
        },
        [onRecordingItemPress],
    );

    useEffect(() => {
        if (isVisible) {
            setAccessibilityFocusTo(accessibleViewRef);
        }
    }, [isVisible]);

    if (!isVisible) {
        return null;
    }

    return (
        <View style={StyleSheet.absoluteFill}>
            <Pressable
                style={StyleSheet.absoluteFill}
                accessibilityLabel={t(
                    ACCESSIBILITY_PLAYER_CLOSE_RECORDINGS_OVERLAY,
                )}
                onPress={onClose}
                ref={accessibleViewRef}
                // @ts-ignore
                tabIndex={-1}
            />
            <Animated.View style={[styles.container, positionStyle]}>
                <View style={[styles.blockArrow, rightPositionForArrow]} />
                <Section
                    title={t(SCREEN_PLAYER_MANAGE_RECORDING_OVERLAY_TITLE)}
                    data={recordingItems}
                    renderItem={renderRecordingItem}
                />
            </Animated.View>
        </View>
    );
};

export default RecordingActionsDropDown;
